// Centralized debug logger with namespaced categories
// Flags (localStorage override, then env):
// - App/UI logs:   caseflow.debug.app (REACT_APP_DEBUG_APP)
// - API logs:      caseflow.debug.api (REACT_APP_DEBUG_API)
// - Master flag:   caseflow.debug (REACT_APP_DEBUG) used as fallback

const isEnvDebug = ((): boolean => {
  try {
    if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DEBUG) {
      return String(process.env.REACT_APP_DEBUG).toLowerCase() === 'true';
    }
  } catch {}
  return false;
})();

function readFlag(): boolean {
  try {
    const v = localStorage.getItem('caseflow.debug');
    if (v == null) return false; // default OFF for cleaner console
    return v === 'true';
  } catch {
    return isEnvDebug || false; // default OFF when localStorage not available
  }
}

function readScopedFlag(lsKey: string, envKey: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(lsKey);
    if (v != null) return v === 'true';
  } catch {}
  try {
    // @ts-ignore
    const ev = typeof process !== 'undefined' && process.env ? process.env[envKey] : undefined;
    if (ev != null) return String(ev).toLowerCase() === 'true';
  } catch {}
  return fallback;
}

let masterEnabled = readFlag();
let appEnabled = readScopedFlag('caseflow.debug.app', 'REACT_APP_DEBUG_APP', masterEnabled);
let apiEnabled = readScopedFlag('caseflow.debug.api', 'REACT_APP_DEBUG_API', masterEnabled);

export function setDebugEnabled(value: boolean) {
  masterEnabled = !!value;
  try { localStorage.setItem('caseflow.debug', String(masterEnabled)); } catch {}
  // Do not overwrite scoped flags; they can be set independently
}

export function isDebugEnabled() { return masterEnabled; }
export function setAppDebugEnabled(value: boolean) {
  appEnabled = !!value; try { localStorage.setItem('caseflow.debug.app', String(appEnabled)); } catch {}
}
export function setApiDebugEnabled(value: boolean) {
  apiEnabled = !!value; try { localStorage.setItem('caseflow.debug.api', String(apiEnabled)); } catch {}
}
export function isAppDebugEnabled() { return appEnabled; }
export function isApiDebugEnabled() { return apiEnabled; }

type LogFn = (message?: any, ...optionalParams: any[]) => void;

function makeLogger(ns: string) {
  const prefix = `[${ns}]`;
  const safe = (fn: LogFn, ...args: any[]) => { try { fn(...args); } catch {} };
  // Decide which flag to use based on namespace
  const useApi = ns === 'API' || ns.startsWith('API:');
  const isEnabled = () => useApi ? apiEnabled : appEnabled;
  return {
    debug: (...args: any[]) => { if (isEnabled()) safe(console.debug, prefix, ...args); },
    info:  (...args: any[]) => { if (isEnabled()) safe(console.info,  prefix, ...args); },
    warn:  (...args: any[]) => { if (isEnabled()) safe(console.warn,  prefix, ...args); },
    error: (...args: any[]) => { if (isEnabled()) safe(console.error, prefix, ...args); },
  };
}

// Predefined namespaces
export const logApp = makeLogger('App');
export const logCaseLog = makeLogger('CaseLog');
export const logCaseDetails = makeLogger('CaseDetailsModal');
export const logAPI = makeLogger('API');
export const logRequests = makeLogger('CaseRequests');

// Ad-hoc namespaces
export function ns(name: string) { return makeLogger(name); }
export {};
